import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { Badge, Button as BSButton, Form, InputGroup } from 'react-bootstrap';
import {
    ReloadOutlined,
    SearchOutlined,
    SolutionOutlined,
    SafetyOutlined,
    ProjectOutlined,
    DollarCircleOutlined,
    MailOutlined,
    PhoneOutlined,
    InfoCircleOutlined,
    AppstoreOutlined,
    ArrowRightOutlined
} from '@ant-design/icons';
import { Button } from 'antd';
import { oneKlicks } from "../SDK/oneclick";
import useInsurancePayment from "../hooks/useInsurancePayment";

const normaliseResponse = (response) => {
    if (!response?.success) {
        return [];
    }
    if (Array.isArray(response?.data)) {
        return response.data;
    }
    if (Array.isArray(response?.data?.items)) {
        return response.data.items;
    }
    return [];
};

const formatCurrency = (value) => {
    if (value === null || value === undefined || value === "") {
        return "—";
    }
    const amount = Number(value);
    if (Number.isNaN(amount)) {
        return value;
    }
    return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        minimumFractionDigits: 0,
    }).format(amount);
};

const calculateMonthlyPremium = (policy) => {
    if (!policy) {
        return 0;
    }
    const annual = Number(
        policy.premium_amount ??
        policy.annual_premium ??
        policy.coverage_amount ??
        policy.amount ??
        0
    );
    if (!Number.isFinite(annual) || annual <= 0) {
        return 0;
    }
    return Math.round((annual / 12) * 100) / 100;
};

const UserInsuranceDirectory = () => {
    const [companies, setCompanies] = useState([]);
    const [policies, setPolicies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const navigate = useNavigate();
    const { startPayment, processingKey } = useInsurancePayment();

    useEffect(() => {
        fetchDirectoryData();
    }, []);

    const fetchDirectoryData = async () => {
        setLoading(true);
        try {
            const [companyRes, policyRes] = await Promise.all([
                oneKlicks.read("", "insurance_companies", {}, "created_at DESC", "", "", "0,200"),
                oneKlicks.read("", "insurance_policies", {}, "created_at DESC", "", "", "0,200"),
            ]);
            setCompanies(normaliseResponse(companyRes));
            setPolicies(normaliseResponse(policyRes));
        } catch (error) {
            console.error("Error loading insurance directory:", error);
            toast.error("Unable to load insurance companies right now");
            setCompanies([]);
            setPolicies([]);
        } finally {
            setLoading(false);
        }
    };

    const policiesByCompany = useMemo(() => {
        return policies.reduce((acc, policy) => {
            const key = policy?.company_id || "unassigned";
            if (!acc[key]) {
                acc[key] = [];
            }
            acc[key].push(policy);
            return acc;
        }, {});
    }, [policies]);

    const filteredCompanies = useMemo(() => {
        if (!searchTerm.trim()) {
            return companies;
        }
        const lowerSearch = searchTerm.toLowerCase();
        return companies.filter((company) => {
            const name = (company.company_name || company.name || "").toLowerCase();
            const legal = (company.legal_name || "").toLowerCase();
            return name.includes(lowerSearch) || legal.includes(lowerSearch);
        });
    }, [companies, searchTerm]);

    const directoryStats = useMemo(() => {
        if (!policies.length) {
            return {
                totalPolicies: 0,
                activePolicies: 0,
                averagePremium: 0,
                companiesAvailable: companies.length,
            };
        }
        const activePolicies = policies.filter((policy) => policy.status === "active").length;
        const premiumSum = policies.reduce((acc, policy) => acc + (Number(policy.premium_amount) || 0), 0);
        return {
            totalPolicies: policies.length,
            activePolicies,
            averagePremium: premiumSum / policies.length,
            companiesAvailable: companies.length,
        };
    }, [policies, companies.length]);

    const handleViewPolicies = (company) => {
        if (!company?.id) {
            toast.error("Unable to open company policies.");
            return;
        }
        navigate(`/user-insurance/companies/${company.id}`, { state: { company } });
    };

    const handleStartPayTrial = (company) => {
        if (!company?.id) {
            toast.error("Missing company details for payment.");
            return;
        }
        const companyPolicies = policiesByCompany[company.id] || [];
        const selectedPolicy = companyPolicies[0];
        if (!selectedPolicy) {
            toast.info("This company has not published a payable policy yet.");
            return;
        }
        const monthlyPremium = calculateMonthlyPremium(selectedPolicy);
        if (!monthlyPremium) {
            toast.error("Premium details for this policy are not available yet.");
            return;
        }
        startPayment({
            referenceId: `directory-${company.id}`,
            amount: monthlyPremium,
            policyMeta: {
                policyId: selectedPolicy.id,
                policyNumber: selectedPolicy.policy_number || selectedPolicy.policy_code || `POL-${selectedPolicy.id}`,
                product: selectedPolicy.coverage_type || selectedPolicy.product_name || company.company_name,
                insurer: company.company_name || company.name,
                companyId: company.id,
                checkoutTitle: company.company_name || "Insurance Payment",
                notes: "Monthly premium payment",
            },
            description: `Monthly premium for ${selectedPolicy.policy_number || selectedPolicy.coverage_type || "insurance plan"}`,
        });
    };

    const getStatusBadge = (status) => {
        const s = (status || "").toLowerCase();
        let variant = "info", label = status || "Unknown";
        if (s === "active") variant = "success";
        else if (s === "inactive" || s === "expired") variant = "danger";
        else if (s === "pending") variant = "warning";
        return (
            <Badge
                bg={variant}
                style={{
                    borderRadius: "5px",
                    padding: "5px 10px",
                    fontSize: "11px",
                    fontWeight: "600",
                    textTransform: "capitalize"
                }}
            >
                {label}
            </Badge>
        );
    };

    return (
        <section className="dc-haslayout dc-dbsectionspace">
            <style>
                {`
                    @media (max-width: 767px) {
                        .dc-table-responsive thead {
                            display: none;
                        }
                        .dc-table-responsive tbody tr {
                            display: block;
                            margin-bottom: 20px;
                            border: 1px solid #eee;
                            border-radius: 10px;
                            background: #fff;
                            box-shadow: 0 4px 12px rgba(0,0,0,0.05);
                            padding: 10px;
                        }
                        .dc-table-responsive tbody td {
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                            padding: 12px 15px !important;
                            border-bottom: 1px solid #f5f5f5;
                            text-align: right;
                            font-size: 13px;
                            min-height: 45px;
                        }
                        .dc-table-responsive tbody td:last-child {
                            border-bottom: 0;
                            margin-top: 10px;
                        }
                        .dc-table-responsive tbody td::before {
                            content: attr(data-label);
                            font-weight: 700;
                            color: #666;
                            text-transform: uppercase;
                            font-size: 10px;
                            letter-spacing: 0.5px;
                            text-align: left;
                            flex: 1;
                        }
                        .dc-table-responsive .d-flex.align-items-center {
                            justify-content: flex-end;
                            width: 100%;
                        }
                        .subbody {
                            padding: 15px !important;
                        }
                    }
                    .dc-metric-card {
                        transition: all 0.3s ease;
                        border: none;
                        box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    }
                    .dc-metric-card:hover {
                        transform: translateY(-5px);
                        box-shadow: 0 8px 24px rgba(0,0,0,0.08);
                    }
                    .dc-btn-primary-modern {
                        background-color: var(--themecolor) !important;
                        border-color: var(--themecolor) !important;
                        color: #fff !important;
                        font-weight: 600;
                        border-radius: 5px;
                        transition: all 0.3s ease;
                    }
                    .dc-btn-primary-modern:hover {
                        background-color: #fff !important;
                        color: var(--themecolor) !important;
                        border-color: var(--themecolor) !important;
                    }
                `}
            </style>
            <div className="subbody" style={{ width: '100%', padding: '2% 4%' }}>
                <div className="container-fluid py-3">

                    {/* Header Card */}
                    <div className="card mb-4" style={{ borderRadius: '5px', border: 'none', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
                        <div className="card-body" style={{ padding: '24px 30px' }}>
                            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
                                <div className="d-flex align-items-center gap-2">
                                    <h5 className="mb-0 font-weight-bold">Insurance Directory</h5>
                                    <Badge bg="info" style={{ borderRadius: '5px', padding: '5px 10px', fontSize: '12px' }}>Catalog</Badge>
                                </div>
                                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <BSButton
                                            variant="outline-primary"
                                            onClick={fetchDirectoryData}
                                            style={{ height: '40px', borderRadius: "5px", display: 'flex', alignItems: 'center', gap: '8px' }}
                                        >
                                            <ReloadOutlined /> Refresh
                                        </BSButton>
                                    </div>
                                </div>
                            </div>

                            <div className="d-flex flex-wrap align-items-center gap-3">
                                <div style={{ flex: '1', minWidth: '280px', maxWidth: '400px' }}>
                                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500', color: '#495057' }}>
                                        Search Partners
                                    </label>
                                    <InputGroup>
                                        <InputGroup.Text style={{ backgroundColor: '#fff', borderRight: 'none', borderRadius: '5px 0 0 5px' }}>
                                            <SearchOutlined style={{ color: '#666' }} />
                                        </InputGroup.Text>
                                        <Form.Control
                                            placeholder="Search by company name or legal name..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            style={{ height: '38px', borderLeft: 'none', borderRadius: "0 5px 5px 0", fontSize: '14px' }}
                                        />
                                    </InputGroup>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Metrics Section */}
                    <div className="row g-4 mb-4">
                        {[
                            { label: "Total Policies", value: directoryStats.totalPolicies, icon: <ProjectOutlined />, color: "#6366f1", bg: "#eef2ff" },
                            { label: "Active Plans", value: directoryStats.activePolicies, icon: <SafetyOutlined />, color: "#10b981", bg: "#ecfdf5" },
                            { label: "Partner Companies", value: directoryStats.companiesAvailable, icon: <SolutionOutlined />, color: "#f59e0b", bg: "#fffbeb" },
                            { label: "Avg. Premium", value: formatCurrency(directoryStats.averagePremium), icon: <DollarCircleOutlined />, color: "#ec4899", bg: "#fdf2f8" }
                        ].map((m, idx) => (
                            <div className="col-12 col-sm-6 col-md-3" key={idx}>
                                <div className="card dc-metric-card h-100" style={{ borderRadius: '10px' }}>
                                    <div className="card-body p-4 d-flex align-items-center gap-3">
                                        <div style={{ width: '45px', height: '45px', borderRadius: '10px', backgroundColor: m.bg, color: m.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>
                                            {m.icon}
                                        </div>
                                        <div>
                                            <small className="text-muted d-block font-weight-bold mb-1" style={{ fontSize: '10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>{m.label}</small>
                                            <h5 className="mb-0 font-weight-bold" style={{ fontSize: '18px' }}>{m.value}</h5>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Table Card */}
                    <div className="card" style={{ borderRadius: '5px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                        <div className="card-header bg-white border-bottom p-4">
                            <div className="d-flex align-items-center gap-2">
                                <AppstoreOutlined style={{ color: 'var(--themecolor)', fontSize: '18px' }} />
                                <h6 className="mb-0 font-weight-bold">Partner Catalog</h6>
                            </div>
                        </div>
                        <div className="card-body p-0">
                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p className="mt-2 text-muted">Discovering partners...</p>
                                </div>
                            ) : filteredCompanies.length === 0 ? (
                                <div className="text-center py-5">
                                    <div className="dc-emptydata-holder">
                                        <div className="dc-emptydata">
                                            <div className="dc-emptydetails dc-empty-booking dc-emptyholder-sm">
                                                <span></span>
                                                <em className="text-muted">No insurance partners found</em>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover mb-0 align-middle dc-table-responsive">
                                        <thead className="bg-light">
                                            <tr style={{ whiteSpace: 'nowrap' }}>
                                                <th style={{ padding: '12px 20px', fontSize: '13px', fontWeight: '600', color: '#666' }}>Provider</th>
                                                <th style={{ padding: '12px 20px', fontSize: '13px', fontWeight: '600', color: '#666' }}>Contact Info</th>
                                                <th style={{ padding: '12px 20px', fontSize: '13px', fontWeight: '600', color: '#666' }}>Status</th>
                                                <th style={{ padding: '12px 20px', fontSize: '13px', fontWeight: '600', color: '#666', textAlign: 'center' }}>Plans</th>
                                                <th style={{ padding: '12px 20px', fontSize: '13px', fontWeight: '600', color: '#666' }}>Min. Monthly</th>
                                                <th style={{ padding: '12px 20px', fontSize: '13px', fontWeight: '600', color: '#666', textAlign: 'right' }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="border-top-0">
                                            {filteredCompanies.map(company => {
                                                const companyPolicies = policiesByCompany[company.id] || [];
                                                const monthlyValues = companyPolicies.map(p => calculateMonthlyPremium(p)).filter(v => v > 0);
                                                const minMonthly = monthlyValues.length ? Math.min(...monthlyValues) : 0;

                                                return (
                                                    <tr key={company.id}>
                                                        <td data-label="Provider" style={{ padding: '12px 20px' }}>
                                                            <div className="d-flex align-items-center gap-3">
                                                                <div style={{ width: '40px', height: '40px', borderRadius: '8px', backgroundColor: '#f0f4f8', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', color: 'var(--themecolor)', fontSize: '16px' }}>
                                                                    {(company.company_name || company.name || '?').charAt(0)}
                                                                </div>
                                                                <div className="text-end text-md-start">
                                                                    <div style={{ fontWeight: '600', color: '#333', fontSize: '13px' }}>{company.company_name || company.name}</div>
                                                                    <div style={{ fontSize: '11px', color: '#999' }}>{company.legal_name || 'Verified Partner'}</div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td data-label="Contact" style={{ padding: '12px 20px' }}>
                                                            <div className="d-flex flex-column gap-1 text-end text-md-start">
                                                                <div style={{ fontSize: '12px', color: '#4b5563' }}>
                                                                    <MailOutlined style={{ fontSize: '11px', marginRight: '6px', color: '#9ca3af' }} />
                                                                    {company.email || 'N/A'}
                                                                </div>
                                                                <div style={{ fontSize: '12px', color: '#4b5563' }}>
                                                                    <PhoneOutlined style={{ fontSize: '11px', marginRight: '6px', color: '#9ca3af' }} />
                                                                    {company.phone || 'N/A'}
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td data-label="Status" style={{ padding: '12px 20px' }}>
                                                            {getStatusBadge(company.status)}
                                                        </td>
                                                        <td data-label="Plans" style={{ padding: '12px 20px', textAlign: 'center' }}>
                                                            <Badge bg="light" text="dark" style={{ border: '1px solid #eee', fontWeight: '500', fontSize: '11px', padding: '5px 10px' }}>
                                                                {companyPolicies.length} Active Plans
                                                            </Badge>
                                                        </td>
                                                        <td data-label="Min. Monthly" style={{ padding: '12px 20px' }}>
                                                            <div style={{ fontWeight: '700', color: '#111827', fontSize: '14px' }}>
                                                                {minMonthly ? formatCurrency(minMonthly) : '—'}
                                                            </div>
                                                        </td>
                                                        <td data-label="Actions" style={{ padding: '12px 20px' }}>
                                                            <div className="d-flex gap-2 justify-content-end align-items-center">
                                                                <Button
                                                                    onClick={() => handleViewPolicies(company)}
                                                                    style={{ borderRadius: '5px', height: '34px', fontSize: '12px', fontWeight: '500' }}
                                                                >
                                                                    View Details
                                                                </Button>
                                                                <BSButton
                                                                    className="dc-btn-primary-modern"
                                                                    style={{ height: '34px', padding: '0 15px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}
                                                                    onClick={() => handleStartPayTrial(company)}
                                                                    disabled={processingKey === `directory-${company.id}`}
                                                                >
                                                                    {processingKey === `directory-${company.id}` ? (
                                                                        <div className="spinner-border spinner-border-sm" role="status"></div>
                                                                    ) : (
                                                                        <>Start Trial <ArrowRightOutlined style={{ fontSize: '10px' }} /></>
                                                                    )}
                                                                </BSButton>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default UserInsuranceDirectory;

